import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Paperclip, X } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { errMsg } from "@/lib/download";

export const fieldCls = "w-full bg-white text-[#0D2B5A] rounded-xl px-4 py-3 text-sm border border-slate-200 focus:border-gold outline-none";

const pill = (on) => `px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${on ? "bg-aic-navy text-white border-[#0D2B5A]" : "bg-white border-slate-300 hover:border-gold"}`;

export const YesNo = ({ value, onChange, testId }) => (
  <div className="flex gap-3" data-testid={testId}>
    {[[true, "Sì"], [false, "No"]].map(([v, l]) => (
      <button key={l} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v ? "yes" : "no"}`} className={`${pill(value === v)} min-w-[96px] justify-center inline-flex items-center gap-1`}>
        {value === v && <Check className="w-4 h-4"/>}{l}
      </button>
    ))}
  </div>
);

export const Chips = ({ options, value, onChange, multi, testId }) => {
  const opts = Array.isArray(options) ? options.map((o) => [o, o]) : Object.entries(options);
  const on = (k) => (multi ? value.includes(k) : value === k);
  const click = (k) => onChange(multi ? (value.includes(k) ? value.filter((x) => x !== k) : [...value, k]) : k);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {opts.map(([k, l]) => <button key={k} type="button" onClick={() => click(k)} data-testid={`${testId}-${String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={pill(on(k))}>{on(k) && <Check className="w-3.5 h-3.5 inline mr-1"/>}{l}</button>)}
    </div>
  );
};

export const Num = ({ value, onChange, testId, placeholder, min = 1, step = 1, className = "" }) => (
  <input type="number" inputMode="numeric" min={min} step={step} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className={`${fieldCls} max-w-[200px] ${className}`}/>
);

export const Follow = ({ label, children }) => (
  <div className="mt-5 pl-4 border-l-2 border-gold space-y-2 animate-in fade-in slide-in-from-top-1 duration-300">
    <div className="text-sm font-semibold text-aic-navy">{label}</div>{children}
  </div>
);

/* value: { url, name } once uploaded, or { file, name } when uploadPath is not given (upload deferred) */
export const FilePick = ({ value, onChange, uploadPath, testId, label = "Allega documento o immagine", imagesOnly }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) { return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10 MB)"); return; }
    if (!uploadPath) { onChange({ file, name: file.name }); return; }
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); onChange({ url: (await api.post(uploadPath, fd)).data.url, name: file.name }); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (value) {
    return (
      <div className="inline-flex items-center gap-2 text-sm bg-[#FBF6EE] border border-gold/40 rounded-full px-4 py-2" data-testid={`${testId}-selected`}>
        <Paperclip className="w-4 h-4 text-gold"/>{value.url ? <a href={mediaUrl(value.url)} target="_blank" rel="noreferrer" className="underline truncate max-w-[200px]">{value.name || "Allegato"}</a> : <span className="truncate max-w-[200px]">{value.name}</span>}
        <button type="button" onClick={() => onChange(null)} data-testid={`${testId}-remove`} aria-label="Rimuovi allegato"><X className="w-4 h-4 text-slate-500"/></button>
      </div>
    );
  }
  return (
    <label className={`btn-pill-outline text-sm cursor-pointer inline-flex ${busy ? "opacity-50" : ""}`}>
      <Paperclip className="w-4 h-4"/>{busy ? "Caricamento…" : label}
      <input type="file" accept={imagesOnly ? "image/jpeg,image/png,image/webp" : "image/jpeg,image/png,image/webp,application/pdf"} onChange={pick} disabled={busy} data-testid={testId} className="hidden"/>
    </label>
  );
};
